import { getToken, clearToken } from '../utils/authToken';
import { useState, useEffect, useCallback } from 'react';
import { API_URL, API } from '../config';
import { 
  Users, FolderGit2, Plus, Trash2, UserPlus, Copy, Check,
  Upload, Image, Code, Eye, Loader2, CheckCircle, XCircle,
  AlertTriangle, Sparkles, ExternalLink, Globe, Lock, X,
  Search, ChevronDown, ChevronUp, Wrench
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';


const GroupsProjects = ({ isVisible, onClose }) => {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('groups');
  const [groups, setGroups] = useState([]);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [copiedCode, setCopiedCode] = useState(false);
  
  // Form states
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDesc, setNewProjectDesc] = useState('');
  const [newProjectVisibility, setNewProjectVisibility] = useState('private');
  const [newProjectTech, setNewProjectTech] = useState('');
  const [newProjectGroup, setNewProjectGroup] = useState('');
  
  const isSpanish = language === 'es';
  
  const getAuthHeaders = () => {
    const token = getToken();
    return {};  // P2.1 cookie-only via axios.defaults
  };
  
  // Fetch data
  const fetchGroups = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/groups/my-groups`, {
        headers: getAuthHeaders()
      });
      if (response.data.success) {
        setGroups(response.data.groups);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  }, []);
  
  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects/my-projects`, {
        headers: getAuthHeaders()
      });
      if (response.data.success) {
        setProjects(response.data.projects);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  }, []);
  
  useEffect(() => {
    if (isVisible) {
      setIsLoading(true);
      Promise.all([fetchGroups(), fetchProjects()]).finally(() => setIsLoading(false));
    }
  }, [isVisible, fetchGroups, fetchProjects]);

  // Botón "Refrescar Dashboard" del header — re-carga grupos y proyectos
  // sin recargar la página, solo cuando el panel está visible.
  useEffect(() => {
    const onRefresh = () => {
      if (!isVisible) return;
      Promise.all([fetchGroups(), fetchProjects()]).catch(() => {});
    };
    window.addEventListener('damyxia:refresh-dashboard', onRefresh);
    return () => window.removeEventListener('damyxia:refresh-dashboard', onRefresh);
  }, [isVisible, fetchGroups, fetchProjects]);
  
  // Create group
  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      toast.error(isSpanish ? 'Nombre del grupo requerido' : 'Group name required');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/groups/create`, {
        name: newGroupName,
        description: newGroupDesc
      }, { headers: getAuthHeaders() });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setGroups([response.data.group, ...groups]);
        setShowCreateGroup(false);
        setNewGroupName('');
        setNewGroupDesc('');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (isSpanish ? 'Error creando grupo' : 'Error creating group'));
    }
  };
  
  // Create project
  const handleCreateProject = async () => {
    if (!newProjectName.trim() || !newProjectDesc.trim()) {
      toast.error(isSpanish ? 'Nombre y descripción requeridos' : 'Name and description required');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/projects/create`, {
        name: newProjectName,
        description: newProjectDesc,
        visibility: newProjectVisibility,
        technologies: newProjectTech.split(',').map(t => t.trim()).filter(Boolean),
        group_id: newProjectGroup || null
      }, { headers: getAuthHeaders() });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setProjects([response.data.project, ...projects]);
        setShowCreateProject(false);
        setNewProjectName('');
        setNewProjectDesc('');
        setNewProjectTech('');
        setNewProjectGroup('');
        setSelectedProject(response.data.project);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (isSpanish ? 'Error creando proyecto' : 'Error creating project'));
    }
  };
  
  // Search users
  const handleSearchUsers = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/groups/search/users?q=${encodeURIComponent(query)}`, {
        headers: getAuthHeaders()
      });
      if (response.data.success) {
        setSearchResults(response.data.users);
      }
    } catch (error) {
      console.error('Search error:', error);
    }
  };
  
  // Invite user
  const handleInviteUser = async (userId) => {
    if (!selectedGroup) return;
    
    try {
      const response = await axios.post(`${API}/groups/${selectedGroup.id}/invite`, {
        user_id: userId
      }, { headers: getAuthHeaders() });
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchGroups();
        setSearchQuery('');
        setSearchResults([]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (isSpanish ? 'Error invitando usuario' : 'Error inviting user'));
    }
  };
  
  // Copy invite code
  const handleCopyInviteCode = (code) => {
    navigator.clipboard.writeText(`${window.location.origin}/join/${code}`);
    setCopiedCode(true);
    toast.success(isSpanish ? 'Código copiado' : 'Code copied');
    setTimeout(() => setCopiedCode(false), 2000);
  };
  
  // Upload code
  const handleUploadCode = async (e) => {
    if (!selectedProject) return;
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${API}/projects/${selectedProject.id}/upload-code`,
        formData,
        { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchProjects();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (isSpanish ? 'Error subiendo código' : 'Error uploading code'));
    } finally {
      setIsLoading(false);
    }
  };
  
  // Upload screenshot
  const handleUploadScreenshot = async (e) => {
    if (!selectedProject) return;
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${API}/projects/${selectedProject.id}/upload-screenshot`,
        formData,
        { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchProjects();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (isSpanish ? 'Error subiendo imagen' : 'Error uploading image'));
    } finally {
      setIsLoading(false);
    }
  };
  
  // Request review
  const handleRequestReview = async (autoFix = false) => {
    if (!selectedProject) return;
    
    try {
      setIsLoading(true);
      toast.info(isSpanish ? '🔍 DamyxIA está revisando tu proyecto...' : '🔍 DamyxIA is reviewing your project...');
      
      const response = await axios.post(
        `${API}/projects/${selectedProject.id}/request-review`,
        { auto_fix: autoFix },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        const review = response.data.review;
        if (response.data.status === 'approved') {
          toast.success(isSpanish ? '✅ ¡Proyecto aprobado!' : '✅ Project approved!');
        } else {
          toast.warning(isSpanish ? '⚠️ Proyecto necesita correcciones' : '⚠️ Project needs corrections');
        }
        
        if (response.data.fixed_files?.length > 0) {
          toast.success(isSpanish 
            ? `🔧 DamyxIA corrigió ${response.data.fixed_files.length} archivos`
            : `🔧 DamyxIA fixed ${response.data.fixed_files.length} files`
          );
        }
        
        // Update project in list
        setProjects(projects.map(p => 
          p.id === selectedProject.id 
            ? { ...p, status: response.data.status, review }
            : p
        ));
        setSelectedProject({ ...selectedProject, status: response.data.status, review });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (isSpanish ? 'Error en la revisión' : 'Error in review'));
    } finally {
      setIsLoading(false);
    }
  };
  
  // Publish project
  const handlePublish = async () => {
    if (!selectedProject) return;
    
    try {
      const response = await axios.post(
        `${API}/projects/${selectedProject.id}/publish`,
        {},
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchProjects();
        setSelectedProject({ ...selectedProject, status: 'published' });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (isSpanish ? 'Error publicando' : 'Error publishing'));
    }
  };
  
  // Delete group
  const handleDeleteGroup = async (groupId) => {
    if (!confirm(isSpanish ? '¿Eliminar este grupo?' : 'Delete this group?')) return;
    
    try {
      await axios.delete(`${API}/groups/${groupId}`, { headers: getAuthHeaders() });
      toast.success(isSpanish ? 'Grupo eliminado' : 'Group deleted');
      setGroups(groups.filter(g => g.id !== groupId));
      setSelectedGroup(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || (isSpanish ? 'Error' : 'Error'));
    }
  };
  
  // Delete project
  const handleDeleteProject = async (projectId) => {
    if (!confirm(isSpanish ? '¿Eliminar este proyecto?' : 'Delete this project?')) return;
    
    try {
      await axios.delete(`${API}/projects/${projectId}`, { headers: getAuthHeaders() });
      toast.success(isSpanish ? 'Proyecto eliminado' : 'Project deleted');
      setProjects(projects.filter(p => p.id !== projectId));
      setSelectedProject(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || (isSpanish ? 'Error' : 'Error'));
    }
  };
  
  const getStatusBadge = (status) => {
    const badges = {
      draft: { color: 'bg-zinc-600', icon: null, text: isSpanish ? 'Borrador' : 'Draft' },
      pending_review: { color: 'bg-yellow-600', icon: Loader2, text: isSpanish ? 'En revisión' : 'Reviewing' },
      approved: { color: 'bg-green-600', icon: CheckCircle, text: isSpanish ? 'Aprobado' : 'Approved' },
      rejected: { color: 'bg-red-600', icon: XCircle, text: isSpanish ? 'Rechazado' : 'Rejected' },
      published: { color: 'bg-indigo-600', icon: Globe, text: isSpanish ? 'Publicado' : 'Published' }
    };
    const badge = badges[status] || badges.draft;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs text-white ${badge.color}`}>
        {Icon && <Icon className="w-3 h-3" />}
        {badge.text}
      </span>
    );
  };
  
  if (!isVisible) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-600/20 rounded-xl flex items-center justify-center">
              <FolderGit2 className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-zinc-100">
                {isSpanish ? 'Grupos y Proyectos' : 'Groups & Projects'}
              </h2>
              <p className="text-xs text-zinc-500">
                {isSpanish ? 'Colabora y publica tus trabajos' : 'Collaborate and publish your work'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b border-zinc-700">
          <button
            onClick={() => setActiveTab('groups')}
            className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
              activeTab === 'groups' 
                ? 'text-indigo-400 border-b-2 border-indigo-500 bg-indigo-500/10' 
                : 'text-zinc-400 hover:text-zinc-200'
            }`}
          >
            <Users className="w-4 h-4" />
            {isSpanish ? 'Mis Grupos' : 'My Groups'}
            <span className="bg-zinc-700 px-1.5 py-0.5 rounded text-xs">{groups.length}</span>
          </button>
          <button
            onClick={() => setActiveTab('projects')}
            className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
              activeTab === 'projects' 
                ? 'text-indigo-400 border-b-2 border-indigo-500 bg-indigo-500/10' 
                : 'text-zinc-400 hover:text-zinc-200'
            }`}
          >
            <FolderGit2 className="w-4 h-4" />
            {isSpanish ? 'Mis Proyectos' : 'My Projects'}
            <span className="bg-zinc-700 px-1.5 py-0.5 rounded text-xs">{projects.length}/2</span>
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
            </div>
          )}
          
          {/* Groups Tab */}
          {activeTab === 'groups' && !isLoading && (
            <div className="space-y-4">
              {/* Create Group Button */}
              <button
                onClick={() => setShowCreateGroup(true)}
                className="w-full p-4 border-2 border-dashed border-zinc-700 hover:border-indigo-500 rounded-xl flex items-center justify-center gap-2 text-zinc-400 hover:text-indigo-400 transition-colors"
              >
                <Plus className="w-5 h-5" />
                {isSpanish ? 'Crear Nuevo Grupo' : 'Create New Group'}
              </button>
              
              {/* Create Group Form */}
              {showCreateGroup && (
                <div className="bg-zinc-800/50 rounded-xl p-4 space-y-3">
                  <input
                    type="text"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    placeholder={isSpanish ? 'Nombre del grupo' : 'Group name'}
                    className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-zinc-200 text-sm"
                  />
                  <textarea
                    value={newGroupDesc}
                    onChange={(e) => setNewGroupDesc(e.target.value)}
                    placeholder={isSpanish ? 'Descripción (opcional)' : 'Description (optional)'}
                    className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-zinc-200 text-sm resize-none"
                    rows={2}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleCreateGroup}
                      className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm"
                    >
                      {isSpanish ? 'Crear' : 'Create'}
                    </button>
                    <button
                      onClick={() => setShowCreateGroup(false)}
                      className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-zinc-200 rounded-lg text-sm"
                    >
                      {isSpanish ? 'Cancelar' : 'Cancel'}
                    </button>
                  </div>
                </div>
              )}
              
              {/* Groups List */}
              {groups.map(group => (
                <div 
                  key={group.id}
                  className={`bg-zinc-800/50 rounded-xl p-4 cursor-pointer transition-colors ${
                    selectedGroup?.id === group.id ? 'ring-2 ring-indigo-500' : 'hover:bg-zinc-800'
                  }`}
                  onClick={() => setSelectedGroup(selectedGroup?.id === group.id ? null : group)}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium text-zinc-100">{group.name}</h3>
                      <p className="text-sm text-zinc-400 mt-1">{group.description}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-zinc-500">
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {group.member_count}/5 {isSpanish ? 'miembros' : 'members'}
                        </span>
                        <span className="flex items-center gap-1">
                          <FolderGit2 className="w-3 h-3" />
                          {group.projects_count} {isSpanish ? 'proyectos' : 'projects'}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteGroup(group.id); }}
                      className="p-2 hover:bg-red-500/20 rounded-lg text-zinc-500 hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {/* Expanded Group Details */}
                  {selectedGroup?.id === group.id && (
                    <div className="mt-4 pt-4 border-t border-zinc-700 space-y-4">
                      {/* Invite Code */}
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-zinc-400">{isSpanish ? 'Código de invitación:' : 'Invite code:'}</span>
                        <code className="px-2 py-1 bg-zinc-900 rounded text-xs text-indigo-400">{group.invite_code}</code>
                        <button
                          onClick={() => handleCopyInviteCode(group.invite_code)}
                          className="p-1 hover:bg-zinc-700 rounded"
                        >
                          {copiedCode ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-zinc-400" />}
                        </button>
                      </div>
                      
                      {/* Search & Invite */}
                      {group.member_count < 5 && (
                        <div className="space-y-2">
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                            <input
                              type="text"
                              value={searchQuery}
                              onChange={(e) => handleSearchUsers(e.target.value)}
                              placeholder={isSpanish ? 'Buscar usuario por email o nombre...' : 'Search user by email or name...'}
                              className="w-full pl-10 pr-4 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-zinc-200 text-sm"
                            />
                          </div>
                          {searchResults.length > 0 && (
                            <div className="bg-zinc-900 border border-zinc-700 rounded-lg overflow-hidden">
                              {searchResults.map(user => (
                                <button
                                  key={user.id}
                                  onClick={() => handleInviteUser(user.id)}
                                  className="w-full px-3 py-2 flex items-center justify-between hover:bg-zinc-800 text-left"
                                >
                                  <div>
                                    <p className="text-sm text-zinc-200">{user.name || user.email}</p>
                                    <p className="text-xs text-zinc-500">{user.email}</p>
                                  </div>
                                  <UserPlus className="w-4 h-4 text-indigo-400" />
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Members */}
                      <div>
                        <p className="text-sm text-zinc-400 mb-2">{isSpanish ? 'Miembros:' : 'Members:'}</p>
                        <div className="flex flex-wrap gap-2">
                          {group.members?.map((member, i) => (
                            <span 
                              key={i}
                              className={`px-2 py-1 rounded-full text-xs ${
                                member.role === 'owner' 
                                  ? 'bg-indigo-600/20 text-indigo-400' 
                                  : 'bg-zinc-700 text-zinc-300'
                              }`}
                            >
                              {member.name || member.email}
                              {member.role === 'owner' && ' 👑'}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              
              {groups.length === 0 && !showCreateGroup && (
                <p className="text-center text-zinc-500 py-8">
                  {isSpanish ? 'No tienes grupos. ¡Crea uno!' : 'No groups yet. Create one!'}
                </p>
              )}
            </div>
          )}
          
          {/* Projects Tab */}
          {activeTab === 'projects' && !isLoading && (
            <div className="space-y-4">
              {/* Create Project Button */}
              {projects.filter(p => ['approved', 'published'].includes(p.status)).length < 2 && (
                <button
                  onClick={() => setShowCreateProject(true)}
                  className="w-full p-4 border-2 border-dashed border-zinc-700 hover:border-indigo-500 rounded-xl flex items-center justify-center gap-2 text-zinc-400 hover:text-indigo-400 transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  {isSpanish ? 'Crear Nuevo Proyecto' : 'Create New Project'}
                </button>
              )}
              
              {/* Create Project Form */}
              {showCreateProject && (
                <div className="bg-zinc-800/50 rounded-xl p-4 space-y-3">
                  <input
                    type="text"
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    placeholder={isSpanish ? 'Nombre del proyecto' : 'Project name'}
                    className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-zinc-200 text-sm"
                  />
                  <textarea
                    value={newProjectDesc}
                    onChange={(e) => setNewProjectDesc(e.target.value)}
                    placeholder={isSpanish ? 'Descripción del proyecto (mínimo 10 caracteres)' : 'Project description (min 10 chars)'}
                    className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-zinc-200 text-sm resize-none"
                    rows={3}
                  />
                  <input
                    type="text"
                    value={newProjectTech}
                    onChange={(e) => setNewProjectTech(e.target.value)}
                    placeholder={isSpanish ? 'Tecnologías (separadas por coma): React, Python...' : 'Technologies (comma separated): React, Python...'}
                    className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-zinc-200 text-sm"
                  />
                  <div className="flex gap-3">
                    <select
                      value={newProjectVisibility}
                      onChange={(e) => setNewProjectVisibility(e.target.value)}
                      className="flex-1 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-zinc-200 text-sm"
                    >
                      <option value="private">{isSpanish ? '🔒 Privado' : '🔒 Private'}</option>
                      <option value="group">{isSpanish ? '👥 Solo grupo' : '👥 Group only'}</option>
                      <option value="public">{isSpanish ? '🌐 Público' : '🌐 Public'}</option>
                    </select>
                    <select
                      value={newProjectGroup}
                      onChange={(e) => setNewProjectGroup(e.target.value)}
                      className="flex-1 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-zinc-200 text-sm"
                    >
                      <option value="">{isSpanish ? 'Sin grupo' : 'No group'}</option>
                      {groups.map(g => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleCreateProject}
                      className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm"
                    >
                      {isSpanish ? 'Crear Proyecto' : 'Create Project'}
                    </button>
                    <button
                      onClick={() => setShowCreateProject(false)}
                      className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-zinc-200 rounded-lg text-sm"
                    >
                      {isSpanish ? 'Cancelar' : 'Cancel'}
                    </button>
                  </div>
                </div>
              )}
              
              {/* Projects List */}
              {projects.map(project => (
                <div 
                  key={project.id}
                  className={`bg-zinc-800/50 rounded-xl p-4 cursor-pointer transition-colors ${
                    selectedProject?.id === project.id ? 'ring-2 ring-indigo-500' : 'hover:bg-zinc-800'
                  }`}
                  onClick={() => setSelectedProject(selectedProject?.id === project.id ? null : project)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-zinc-100">{project.name}</h3>
                        {getStatusBadge(project.status)}
                      </div>
                      <p className="text-sm text-zinc-400 mt-1 line-clamp-2">{project.description}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-zinc-500">
                        {project.group_name && (
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {project.group_name}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          {project.visibility === 'public' ? <Globe className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                          {project.visibility}
                        </span>
                        <span className="flex items-center gap-1">
                          <Code className="w-3 h-3" />
                          {project.code_files?.length || 0} {isSpanish ? 'archivos' : 'files'}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteProject(project.id); }}
                      className="p-2 hover:bg-red-500/20 rounded-lg text-zinc-500 hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {/* Expanded Project Details */}
                  {selectedProject?.id === project.id && (
                    <div className="mt-4 pt-4 border-t border-zinc-700 space-y-4">
                      {/* Upload Buttons */}
                      {project.status !== 'published' && (
                        <div className="flex gap-2">
                          <label className="flex-1 px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-zinc-200 rounded-lg text-sm flex items-center justify-center gap-2 cursor-pointer">
                            <Code className="w-4 h-4" />
                            {isSpanish ? 'Subir Código (.zip o archivo)' : 'Upload Code (.zip or file)'}
                            <input type="file" className="hidden" onChange={handleUploadCode} accept=".zip,.py,.js,.jsx,.ts,.tsx,.html,.css,.json" />
                          </label>
                          <label className="flex-1 px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-zinc-200 rounded-lg text-sm flex items-center justify-center gap-2 cursor-pointer">
                            <Image className="w-4 h-4" />
                            {isSpanish ? 'Subir Captura' : 'Upload Screenshot'}
                            <input type="file" className="hidden" onChange={handleUploadScreenshot} accept="image/*" />
                          </label>
                        </div>
                      )}
                      
                      {/* Review Section */}
                      {project.review && (
                        <div className="bg-zinc-900/50 rounded-lg p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-zinc-200 flex items-center gap-2">
                              <Sparkles className="w-4 h-4 text-indigo-400" />
                              {isSpanish ? 'Revisión de DamyxIA' : 'DamyxIA Review'}
                            </span>
                            <span className="text-lg font-bold text-indigo-400">{project.review.overall_score}/100</span>
                          </div>
                          <p className="text-sm text-zinc-400">{project.review.summary}</p>
                          
                          {/* Scores */}
                          <div className="grid grid-cols-3 gap-2 text-xs">
                            <div className="bg-zinc-800 rounded p-2 text-center">
                              <p className="text-zinc-500">{isSpanish ? 'Calidad' : 'Quality'}</p>
                              <p className="text-zinc-200 font-medium">{project.review.quality_score}</p>
                            </div>
                            <div className="bg-zinc-800 rounded p-2 text-center">
                              <p className="text-zinc-500">{isSpanish ? 'Contenido' : 'Content'}</p>
                              <p className="text-zinc-200 font-medium">{project.review.content_score}</p>
                            </div>
                            <div className="bg-zinc-800 rounded p-2 text-center">
                              <p className="text-zinc-500">{isSpanish ? 'Funcionalidad' : 'Functionality'}</p>
                              <p className="text-zinc-200 font-medium">{project.review.functionality_score}</p>
                            </div>
                          </div>
                          
                          {/* Errors */}
                          {project.review.errors?.length > 0 && (
                            <div className="space-y-1">
                              <p className="text-xs text-zinc-500 flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                {isSpanish ? 'Errores encontrados:' : 'Errors found:'}
                              </p>
                              {project.review.errors.slice(0, 3).map((err, i) => (
                                <div key={i} className="text-xs bg-red-500/10 text-red-300 px-2 py-1 rounded">
                                  <span className="font-medium">{err.file}:</span> {err.description}
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Fixed indicator */}
                          {project.errors_fixed && (
                            <div className="flex items-center gap-2 text-green-400 text-sm">
                              <CheckCircle className="w-4 h-4" />
                              {isSpanish ? '¡Errores corregidos por DamyxIA!' : 'Errors fixed by DamyxIA!'}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        {project.status === 'draft' && (
                          <>
                            <button
                              onClick={() => handleRequestReview(false)}
                              disabled={isLoading}
                              className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-zinc-700 text-white rounded-lg text-sm flex items-center justify-center gap-2"
                            >
                              <Eye className="w-4 h-4" />
                              {isSpanish ? 'Solicitar Revisión' : 'Request Review'}
                            </button>
                            <button
                              onClick={() => handleRequestReview(true)}
                              disabled={isLoading}
                              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-700 text-white rounded-lg text-sm flex items-center justify-center gap-2"
                              title={isSpanish ? 'DamyxIA revisará y corregirá automáticamente' : 'DamyxIA will review and auto-fix'}
                            >
                              <Wrench className="w-4 h-4" />
                              {isSpanish ? 'Auto-corregir' : 'Auto-fix'}
                            </button>
                          </>
                        )}
                        
                        {project.status === 'rejected' && (
                          <>
                            <button
                              onClick={() => handleRequestReview(true)}
                              disabled={isLoading}
                              className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-700 text-white rounded-lg text-sm flex items-center justify-center gap-2"
                            >
                              <Wrench className="w-4 h-4" />
                              {isSpanish ? 'Corregir con DamyxIA' : 'Fix with DamyxIA'}
                            </button>
                            <button
                              onClick={() => handleRequestReview(false)}
                              disabled={isLoading}
                              className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg text-sm flex items-center justify-center gap-2"
                            >
                              <Eye className="w-4 h-4" />
                              {isSpanish ? 'Re-revisar' : 'Re-review'}
                            </button>
                          </>
                        )}
                        
                        {project.status === 'approved' && (
                          <button
                            onClick={handlePublish}
                            className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm flex items-center justify-center gap-2"
                          >
                            <Upload className="w-4 h-4" />
                            {isSpanish ? '🚀 Publicar Proyecto' : '🚀 Publish Project'}
                          </button>
                        )}
                        
                        {project.status === 'published' && (
                          <div className="flex-1 px-4 py-2 bg-indigo-600/20 text-indigo-400 rounded-lg text-sm flex items-center justify-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            {isSpanish ? '¡Proyecto publicado!' : 'Project published!'}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              
              {projects.length === 0 && !showCreateProject && (
                <p className="text-center text-zinc-500 py-8">
                  {isSpanish ? 'No tienes proyectos. ¡Crea uno!' : 'No projects yet. Create one!'}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GroupsProjects;
